import { useEffect, useCallback } from 'react';
import { RefreshCw, Download, Search } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { useIndexedDB } from '@/hooks/useIndexedDB';
import { loadCompanies } from '@/services/contacts';
import { getCredits } from '@/services/credits';
import {
  Header,
  ScrapeModal,
  DataDrawerWrapper,
  GradeDrawer,
  CompanyTable,
  Pagination,
} from '@/components';

export function RecruiterIntelligence() {
  const {
    userEmail,
    isAuthenticated,
    loading,
    companies,
    filteredCompanies,
    perPage,
    searchQuery,
    gradeFilter,
    recruiterIntelData,
    setLoading,
    setCompanies,
    setCredits,
    setPerPage,
    setSearchQuery,
    setGradeFilter,
    setRecruiterIntelData,
  } = useAppStore();

  const { loadAllIntel, saveIntel } = useIndexedDB();

  // Load companies and intel data
  const loadData = useCallback(async (signal?: AbortSignal) => {
    if (!userEmail) return;

    setLoading(true);
    try {
      // Load credits
      const credits = await getCredits(userEmail);
      if (signal?.aborted) return;
      setCredits(credits);

      // Load intel from IndexedDB
      const intelData = await loadAllIntel();
      if (signal?.aborted) return;
      setRecruiterIntelData(intelData);

      // Load companies from API
      const companyList = await loadCompanies(userEmail, { signal });
      if (signal?.aborted) return;
      setCompanies(companyList);
    } catch (error) {
      if ((error as Error)?.name !== 'AbortError') {
        console.error('Failed to load data:', error);
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [userEmail, setLoading, setCredits, setRecruiterIntelData, setCompanies, loadAllIntel]);

  // Initial load
  useEffect(() => {
    if (isAuthenticated && userEmail) {
      const controller = new AbortController();
      loadData(controller.signal);
      return () => controller.abort();
    }
  }, [isAuthenticated, userEmail, loadData]);

  // Save intel to IndexedDB when it changes
  useEffect(() => {
    Object.entries(recruiterIntelData).forEach(([companyId, intel]) => {
      saveIntel(companyId, intel);
    });
  }, [recruiterIntelData, saveIntel]);

  // Export CSV
  const handleExportCSV = () => {
    if (filteredCompanies.length === 0) {
      alert('No companies to export');
      return;
    }

    const headers = [
      'Company',
      'Contacts',
      'Phone',
      'Email',
      'LinkedIn 360',
      'Glassdoor Rating',
      'Consumer Intel',
      'Crunchbase',
      'Overall Grade',
    ];

    const rows = filteredCompanies.map((company) => {
      const intel = recruiterIntelData[company.companyId] || {};
      const linkedin360 = (intel.linkedin_360?.data as { jobs?: unknown[] })?.jobs?.length || 0;
      const glassdoor = (intel.glassdoor?.data?.overview?.[0]?.rating) || '';
      const consumerIntel = intel.consumer_intel ? 'Yes' : '';
      const crunchbase = intel.crunchbase ? 'Yes' : '';

      return [
        company.companyName,
        company.contacts.length,
        company.phone,
        company.email,
        linkedin360 ? `${linkedin360} jobs` : '',
        glassdoor,
        consumerIntel,
        crunchbase,
        intel.paygrade?.data?.overall_grade || '',
      ];
    });

    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `recruiter_intelligence_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Check auth
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Please Log In</h1>
          <a href="/login" className="text-red-500 hover:text-red-400">
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />

      {/* Page Header */}
      <div className="px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Recruiter Intelligence</h2>
            <p className="text-sm text-gray-400 mt-1">
              Company hiring insights from your researched contacts
            </p>
          </div>
          <div className="text-sm text-gray-400">
            Companies:{' '}
            <span className="text-white font-bold">{companies.length}</span>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-card-dark border-b border-gray-800 px-6 py-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px] max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search company name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500"
            />
          </div>

          {/* Grade Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">Grade:</label>
            <select
              value={gradeFilter}
              onChange={(e) => setGradeFilter(e.target.value)}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            >
              <option value="">All Grades</option>
              <option value="A">A - Excellent</option>
              <option value="B">B - Good</option>
              <option value="C">C - Average</option>
              <option value="D">D - Poor</option>
              <option value="F">F - Bad</option>
            </select>
          </div>

          {/* Per Page */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">Show:</label>
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
            </select>
          </div>

          {/* Refresh Button */}
          <button
            onClick={() => {
              void loadData();
            }}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          {/* Export Button */}
          <button
            onClick={handleExportCSV}
            className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Grade Legend */}
      <div className="px-6 py-2 bg-gray-900 border-b border-gray-800">
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <span className="text-gray-400">Grading:</span>
          <span>
            <span className="px-2 py-0.5 rounded font-bold bg-green-500 text-white">A</span>{' '}
            Excellent (4.5+ rating, competitive pay)
          </span>
          <span>
            <span className="px-2 py-0.5 rounded font-bold bg-lime-500 text-white">B</span>{' '}
            Good (4.0+ rating, transparent)
          </span>
          <span>
            <span className="px-2 py-0.5 rounded font-bold bg-yellow-500 text-black">C</span>{' '}
            Average (3.5+ rating)
          </span>
          <span>
            <span className="px-2 py-0.5 rounded font-bold bg-orange-500 text-white">D</span>{' '}
            Poor (3.0+ rating, issues)
          </span>
          <span>
            <span className="px-2 py-0.5 rounded font-bold bg-red-500 text-white">F</span>{' '}
            Bad (&lt;3.0 rating, red flags)
          </span>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/75 z-40 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-red-500 mx-auto mb-4" />
            <p className="text-white text-lg">Loading companies...</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="px-6 py-4">
        <div className="bg-card-dark border border-border-dark rounded-xl overflow-hidden">
          <div className="max-h-[calc(100vh-340px)] overflow-y-auto">
            <CompanyTable />
          </div>
        </div>
      </div>

      {/* Pagination */}
      <Pagination />

      {/* Footer */}
      <footer className="border-t border-gray-800 px-6 py-4 mt-8">
        <div className="flex justify-between items-center text-sm text-gray-500">
          <div>© 2026 Profitibull. All rights reserved.</div>
          <div className="flex space-x-4">
            <a href="#" className="hover:text-white">
              Terms
            </a>
            <a href="#" className="hover:text-white">
              Privacy
            </a>
            <a href="#" className="hover:text-white">
              Support
            </a>
          </div>
        </div>
      </footer>

      {/* Modals & Drawers */}
      <ScrapeModal />
      <DataDrawerWrapper />
      <GradeDrawer />
    </div>
  );
}
